import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight, BadgeEuro, Cake, Check, FileText, Hourglass, LogIn, LogOut, MailWarning,
  MailX, Sparkles, TriangleAlert,
} from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import * as api from '../data/supabaseAdapter'
import { supabase } from '../lib/supabase'
import type { Booking } from '../data/model'
import { addDays, nights, todayISO } from '../lib/dates'
import { guestLabel, upcomingBirthdays, type GuestProfile } from '../lib/guests'
import { mayReceiveMarketing, type MarketingProfile } from '../lib/marketing'
import { Badge, Card, EmptyState, Stat, cx } from '../ui/kit'
import BookingSheet, { STATUS_TONE } from './BookingSheet'
import { loadThresholds, type RksvThresholds } from './ComplianceCards'

/** How far ahead a lapsing hold is worth mentioning. */
const OPTION_WARN_DAYS = 3

/**
 * Wie weit voraus ein Geburtstag hier auftaucht.
 *
 * VIERZEHN TAGE, und nicht „heute". Ein Geburtstag, von dem der Vermieter am selben
 * Tag erfährt, ist für alles zu spät, was Zeit braucht: eine Karte muss zwei Tage
 * vorher in die Post, ein Anruf will vorbereitet sein, und ein Gutschein, den man am
 * Nachmittag zusammenklickt, sieht danach aus. Zwei Wochen sind der Vorlauf, in dem
 * man noch etwas tun KANN — und kurz genug, dass die Karte nicht dauernd zwanzig
 * Namen zeigt, von denen keiner dringend ist.
 */
const BIRTHDAY_WINDOW_DAYS = 14

export default function TodayView() {
  const { t, lang, formatMoney, formatDate } = useI18n()
  const store = useStore()
  const [active, setActive] = useState<Booking | null>(null)
  const today = todayISO()

  const arrivals = store.db.bookings.filter(
    (b) => b.arrival === today && b.kind === 'booking' && b.status !== 'cancelled')
  const departures = store.db.bookings.filter(
    (b) => b.departure === today && b.kind === 'booking' && b.status !== 'cancelled')

  // Was die heutigen Gäste über den Online-Check-in gemeldet haben.
  const checkins = useCheckins(arrivals.map((b) => b.id))

  const openInvoices = store.db.invoices.filter(
    (i) => i.status !== 'paid' && i.status !== 'cancelled')
  const openTotal = openInvoices.reduce((a, i) => a + (i.total_gross - i.paid_amount), 0)

  const depositsHeld = store.db.bookings
    .filter((b) => b.deposit_status === 'held')
    .reduce((a, b) => a + b.deposit_amount, 0)

  const occupancy = useMemo(() => {
    const units = store.db.units.filter((u) => u.active).length
    const to = addDays(today, 30)
    if (!units) return 0
    const sold = store.bookingsInRange(today, to)
      .filter((b) => b.kind === 'booking'
        && ['confirmed', 'checked_in', 'checked_out', 'option'].includes(b.status))
      .reduce((a, b) => {
        const s = b.arrival > today ? b.arrival : today
        const e = b.departure < to ? b.departure : to
        return a + Math.max(0, nights(s, e))
      }, 0)
    return Math.round((sold / (units * 30)) * 100)
  }, [store, today])

  /**
   * Holds about to lapse. The nightly job releases them on its own, so this is not a
   * chore list — it is the last chance to ring the guest before the unit goes back on
   * sale. Sorted soonest first, because that is the one to call.
   */
  const expiringOptions = useMemo(() => {
    const limit = addDays(today, OPTION_WARN_DAYS)
    return store.db.bookings
      .filter((b) => b.status === 'option' && b.option_until
        && b.option_until >= today && b.option_until <= limit)
      .sort((a, b) => (a.option_until ?? '').localeCompare(b.option_until ?? ''))
  }, [store.db.bookings, today])

  /**
   * The RKSV thresholds, watched rather than waited for. A landlord who takes cards
   * can be over the line without ever having seen a banknote, and the obligation
   * starts three months later — which is only useful if somebody says so in time.
   */
  const [rksv, setRksv] = useState<RksvThresholds | null>(null)
  useEffect(() => {
    if (!store.live || !store.db.tenant.id) return
    void loadThresholds(store.db.tenant.id).then(setRksv).catch(() => { /* silent */ })
  }, [store.live, store.db.tenant.id])

  /**
   * Offene Anzahlungen. Wer nicht zahlt, sagt es nicht — deshalb steht es hier und
   * nicht in der Buchung, wo es erst auffällt, wenn jemand sie ohnehin öffnet.
   */
  const openPrepayments = useMemo(() => store.db.bookings
    .filter((b) => b.kind === 'booking' && !b.prepaid_at
      && (b.prepayment_amount || 0) > 0
      && ['option', 'confirmed'].includes(b.status)
      && b.departure >= today)
    .sort((a, b) => (a.prepayment_due ?? a.arrival).localeCompare(b.prepayment_due ?? b.arrival)),
    [store.db.bookings, today])

  const conflicts = store.db.ical_events.filter((e) => {
    if (e.dismissed || e.booking_id) return false
    return !store.isAvailable(e.unit_id, e.arrival, e.departure)
  })

  return (
    <div className="p-4 pt-safe space-y-4 max-w-3xl">
      <header>
        <h1 className="text-xl font-semibold">{store.db.tenant.name}</h1>
        <p className="text-sm text-muted">{formatDate(today, {
          weekday: 'long', day: 'numeric', month: 'long',
        })}</p>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label={t('dashboard.occupancy30')} value={`${occupancy}%`} />
        <Stat label={t('dashboard.openInvoices')} value={formatMoney(openTotal)}
          tone={openTotal > 0 ? 'text-warn' : undefined} />
        <Stat label={t('dashboard.todayArrivals')} value={arrivals.length} />
        <Stat label={t('deposit.held')} value={formatMoney(depositsHeld)} />
      </div>

      <OpenConfirmationsCard />

      {conflicts.length > 0 && (
        <Card className="p-3 border-warn/40">
          <div className="flex items-center gap-2 text-warn text-sm font-medium mb-2">
            <TriangleAlert size={16} /> {t('calendar.conflict')} ({conflicts.length})
          </div>
          <ul className="text-sm space-y-1">
            {conflicts.map((e) => (
              <li key={e.id} className="text-muted">
                {store.unitById(e.unit_id)?.name} · {formatDate(e.arrival)} – {formatDate(e.departure)}
                <span className="ml-2 text-xs">{e.summary}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {rksv && (rksv.obligated || rksv.closeness > 0.8) && (
        <Card className={cx('p-3', rksv.obligated ? 'border-danger/40' : 'border-warn/40')}>
          <div className={cx('flex items-center gap-2 text-sm font-medium mb-1',
            rksv.obligated ? 'text-danger' : 'text-warn')}>
            <TriangleAlert size={16} />
            {rksv.obligated
              ? (lang === 'de' ? 'Registrierkassenpflicht' : 'Registered till required')
              : (lang === 'de' ? 'Registrierkasse rückt näher' : 'Approaching the till threshold')}
          </div>
          <p className="text-sm text-muted">
            {lang === 'de'
              ? `Umsatz ${formatMoney(rksv.turnover)} (Grenze ${formatMoney(15000)}), davon Barumsatz ${formatMoney(rksv.cash_like)} (Grenze ${formatMoney(7500)}). Karten- und Onlinezahlungen zählen als Barumsatz.`
              : `Turnover ${formatMoney(rksv.turnover)} (limit ${formatMoney(15000)}), of which cash-like ${formatMoney(rksv.cash_like)} (limit ${formatMoney(7500)}). Card and online payments count as cash.`}
            {rksv.obliged_from && (lang === 'de'
              ? ` Pflicht ab ${formatDate(rksv.obliged_from)}.`
              : ` Required from ${formatDate(rksv.obliged_from)}.`)}
          </p>
        </Card>
      )}

      {openPrepayments.length > 0 && (
        <Card className="p-3 border-warn/40">
          <div className="flex items-center gap-2 text-warn text-sm font-medium mb-2">
            <BadgeEuro size={16} />
            {lang === 'de' ? 'Anzahlung offen' : 'Deposit outstanding'} ({openPrepayments.length})
          </div>
          <ul className="text-sm space-y-1">
            {openPrepayments.slice(0, 5).map((b) => {
              const due = b.prepayment_due ?? b.arrival
              const overdue = due < today
              return (
                <li key={b.id}>
                  <button onClick={() => setActive(b)}
                    className="text-left hover:underline underline-offset-2">
                    <span className="font-medium">{store.guestName(b.guest_id)}</span>
                    <span className="text-muted">
                      {' · '}{formatMoney(b.prepayment_amount)}
                      {' · '}{store.unitById(b.unit_id)?.name}
                      {' · '}
                      <span className={cx(overdue && 'text-danger')}>
                        {overdue
                          ? (lang === 'de' ? `fällig seit ${formatDate(due)}` : `due since ${formatDate(due)}`)
                          : (lang === 'de' ? `fällig ${formatDate(due)}` : `due ${formatDate(due)}`)}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </Card>
      )}

      {expiringOptions.length > 0 && (
        <Card className="p-3 border-warn/40">
          <div className="flex items-center gap-2 text-warn text-sm font-medium mb-2">
            <Hourglass size={16} /> {t('booking.optionExpiring')} ({expiringOptions.length})
          </div>
          <ul className="text-sm space-y-1">
            {expiringOptions.map((b) => {
              const days = nights(today, b.option_until!)
              return (
                <li key={b.id}>
                  <button
                    onClick={() => setActive(b)}
                    className="text-left hover:underline underline-offset-2"
                  >
                    <span className="font-medium">{store.guestName(b.guest_id)}</span>
                    <span className="text-muted">
                      {' · '}{store.unitById(b.unit_id)?.name}
                      {' · '}{formatDate(b.arrival)} – {formatDate(b.departure)}
                      {' · '}
                      {days <= 0
                        ? t('booking.optionExpires')
                        : t('booking.optionExpiresIn', { n: days })}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </Card>
      )}

      <BirthdayCard today={today} />

      <Section title={t('calendar.arrivals')} icon={<LogIn size={16} />} bookings={arrivals}
        onPick={setActive} checkins={checkins} />
      <Section title={t('calendar.departures')} icon={<LogOut size={16} />} bookings={departures}
        onPick={setActive} />

      <HousekeepingSection day={today} />

      <BookingSheet booking={active} open={!!active} onClose={() => setActive(null)} />
    </div>
  )
}

/* ══════════════════════════════════════════════════════════════════════════════
 *  VERTRAGSBESTÄTIGUNGEN, DIE NICHT HINAUSGEGANGEN SIND
 * ══════════════════════════════════════════════════════════════════════════════
 *  WARUM DIESE KARTE HIER STEHT UND NICHT IM BUCHUNGSBLATT.
 *
 *  Nimmt ein Gast sein Angebot online an, schuldet der Vermieter ihm eine
 *  Bestätigung des Vertrags auf einem dauerhaften Datenträger — binnen angemessener
 *  Frist, spätestens bei Beginn der Leistung (§ 312f Abs. 2 BGB, § 7 Abs. 3 FAGG).
 *  Diese eine Bestätigung geht weiterhin automatisch hinaus, weil sie der GAST
 *  auslöst und in dem Moment niemand ein Gerät in der Hand hält (`quote-accept`).
 *  Wenn sie scheitert, meist weil beim Gast keine E-Mail-Adresse hinterlegt ist,
 *  bleibt eine gesetzliche Pflicht offen.
 *
 *  Das Buchungsblatt wäre der falsche Ort dafür. Es öffnet nur, wer die Buchung
 *  schon kennt — und das ist genau diese Buchung nicht: sie ist entstanden, ohne
 *  dass der Vermieter ein Wort mit dem Gast gewechselt hätte. Ein Hinweis, den man
 *  erst findet, wenn man ohnehin nachsieht, ist bei einer Frist wertlos.
 *
 *  Die Tagesübersicht wird jeden Morgen geöffnet. Sie ist der einzige Bildschirm
 *  dieses Produkts, bei dem das gilt, und damit der einzige, auf dem eine Frist
 *  rechtzeitig auffällt. Die Karte erscheint nicht, wenn nichts offen ist — eine
 *  dauerhaft sichtbare leere Karte wäre der schnellste Weg, sie unsichtbar zu machen.
 *
 *  NACHSENDEN KANN DIE ANWENDUNG NICHT MEHR. StaySuite verschickt keine Gastpost;
 *  was hier fehlt, trägt der Vermieter selbst nach — Angebot öffnen, drucken oder
 *  als PDF sichern und aus dem eigenen Mailprogramm senden. Die Zeile führt deshalb
 *  auf den Beleg und nicht mehr auf einen Sendeknopf. Ist der Grund eine fehlende
 *  Adresse, führt der Name zum Gast, wo sie nachgetragen wird.
 * ══════════════════════════════════════════════════════════════════════════════ */

interface OpenConfirmation {
  id: string
  number: string
  accepted_at: string | null
  confirmation_error: string
  booking_id: string
}

function OpenConfirmationsCard() {
  const { lang, formatDate } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const [rows, setRows] = useState<OpenConfirmation[]>([])

  const tenantId = store.db.tenant.id

  useEffect(() => {
    if (!store.live || !supabase || !tenantId) return
    let alive = true
    void supabase.from('quotes')
      .select('id, number, accepted_at, confirmation_error, booking_id')
      .eq('tenant_id', tenantId)
      .not('confirmation_error', 'is', null)
      .order('accepted_at', { ascending: false })
      .limit(20)
      .then(({ data }) => {
        if (alive && data) setRows(data as unknown as OpenConfirmation[])
      })
    return () => { alive = false }
  }, [store.live, tenantId])

  /** Der Fehlercode in einem Satz, der sagt, was zu tun ist. */
  const reason = (code: string): string => {
    if (code.startsWith('no_recipient_email')) {
      return de ? 'keine E-Mail-Adresse beim Gast' : 'no e-mail address for this guest'
    }
    if (code.startsWith('tenant_has_no_reply_address')) {
      return de ? 'keine Antwortadresse in den Stammdaten' : 'no reply address in your settings'
    }
    if (code.startsWith('unreachable')) {
      return de ? 'Versand war nicht erreichbar' : 'the mail service was unreachable'
    }
    if (code.startsWith('send_failed')) {
      return de ? 'Versand fehlgeschlagen' : 'sending failed'
    }
    return code
  }

  if (rows.length === 0) return null

  return (
    <Card className="p-3 border-danger/40">
      <div className="flex items-center gap-2 text-danger text-sm font-medium mb-1">
        <MailWarning size={16} />
        {de ? 'Bestätigung nicht zugestellt' : 'Confirmation not delivered'} ({rows.length})
      </div>
      <p className="text-xs text-muted mb-2">
        {de
          ? 'Diese Gäste haben online zugesagt. Die Bestätigung des Vertrags ist gesetzlich geschuldet und noch nicht bei ihnen — spätestens vor der Anreise. Bitte selbst nachholen: Angebot öffnen, drucken oder als PDF sichern und aus Ihrem Mailprogramm senden.'
          : 'These guests accepted online. Confirmation of the contract is required by law and has not reached them yet — at the latest before arrival. Please follow up yourself: open the quote, print it or save it as a PDF, and send it from your own mail program.'}
      </p>
      <ul className="text-sm space-y-2">
        {rows.map((r) => {
          const booking = store.db.bookings.find((b) => b.id === r.booking_id)
          const guestId = booking?.guest_id ?? null
          const name = guestId
            ? store.guestName(guestId)
            : (de ? 'Unbekannter Gast' : 'Unknown guest')
          return (
            <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-2">
              <span>
                {guestId
                  ? (
                    <Link to={`/guests?gast=${guestId}`}
                      className="font-medium hover:underline underline-offset-2">
                      {name}
                    </Link>
                  )
                  : <span className="font-medium">{name}</span>}
                <span className="text-muted">
                  {' · '}{r.number}
                  {r.accepted_at && <>{' · '}{formatDate(r.accepted_at.slice(0, 10))}</>}
                  {' · '}<span className="text-danger">{reason(r.confirmation_error)}</span>
                </span>
              </span>
              {/* Der Weg zum Beleg, nicht zum Versand: von dort aus druckt oder
                  sichert der Vermieter die Bestätigung und schickt sie selbst. */}
              <Link
                to={`/document/quote/${r.id}`}
                className="inline-flex items-center gap-1 text-xs underline underline-offset-2"
              >
                <FileText size={12} />
                {de ? 'Angebot öffnen' : 'Open the quote'}
              </Link>
            </li>
          )
        })}
      </ul>
    </Card>
  )
}

/**
 * Geburtstage der nächsten zwei Wochen.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM HIER UND NICHT NUR IN DER GÄSTELISTE.
 *
 * Beides, aber mit verschiedenen Aufgaben. Die Gästeliste hat einen Filter
 * „Geburtstag" — das ist der Ort, an dem man sich HINSETZT und eine Handvoll Mails
 * schreibt. Diese Karte ist das Gegenteil: sie fragt niemanden, sie steht einfach da,
 * wenn der Vermieter morgens ohnehin nachsieht, wer heute anreist.
 *
 * Ein Geburtstag ist nämlich kein Vorgang, den man sucht. Niemand öffnet die
 * Gästeliste, um nachzuschlagen, ob jemand demnächst Geburtstag hat — man erfährt es
 * oder man erfährt es nicht. Nur die Tagesübersicht wird jeden Tag geöffnet, und nur
 * dort kostet die Information keinen Entschluss.
 *
 * Die Karte erscheint nicht, wenn niemand Geburtstag hat. Eine dauerhaft sichtbare
 * leere Karte wäre der schnellste Weg, sie unsichtbar zu machen.
 * ═══════════════════════════════════════════════════════════════════════════
 */
function BirthdayCard({ today }: { today: string }) {
  const { lang } = useI18n()
  const store = useStore()
  const de = lang === 'de'

  const entries = useMemo(
    () => upcomingBirthdays(store.db.guests as GuestProfile[], today, BIRTHDAY_WINDOW_DAYS),
    [store.db.guests, today],
  )

  if (entries.length === 0) return null

  return (
    <Card className="p-3">
      <div className="flex items-center gap-2 text-sm font-medium mb-2">
        <Cake size={16} className="text-accent" />
        {de ? 'Geburtstag demnächst' : 'Birthdays coming up'}
        <span className="tabular-nums text-muted">({entries.length})</span>
      </div>
      <ul className="text-sm space-y-1">
        {entries.map(({ guest, info }) => {
          // Ob eine Mail überhaupt in Frage kommt, steht gleich daneben. Sonst ist
          // der Weg: klicken, lesen, dass nichts geht, zurück — dreimal am Tag.
          const blocked = !mayReceiveMarketing(guest as MarketingProfile)
          return (
            <li key={guest.id}>
              <Link
                to={`/guests?gast=${guest.id}`}
                className="inline-flex flex-wrap items-baseline gap-x-1.5 hover:underline underline-offset-2"
              >
                <span className="font-medium">
                  {guestLabel(guest, de ? 'Ohne Namen' : 'No name')}
                </span>
                <span className="text-muted">
                  {info.inDays === 0
                    ? (de ? 'hat heute Geburtstag' : 'has a birthday today')
                    : info.inDays === 1
                      ? (de ? 'hat morgen Geburtstag' : 'has a birthday tomorrow')
                      : (de ? `in ${info.inDays} Tagen` : `in ${info.inDays} days`)}
                  {' · '}
                  {de ? `wird ${info.turning}` : `turning ${info.turning}`}
                </span>
                {blocked && (
                  <span className="inline-flex items-center gap-1 text-xs text-muted">
                    <MailX size={12} />
                    {de ? 'keine Werbeeinwilligung' : 'no marketing consent'}
                  </span>
                )}
              </Link>
            </li>
          )
        })}
      </ul>
      <p className="text-xs text-muted mt-2">
        {de
          ? 'Eine Karte muss zwei Tage vorher in die Post; eine Mail ist am Tag selbst gerade recht.'
          : 'A card needs two days in the post; an e-mail is right on the day itself.'}
      </p>
    </Card>
  )
}

/**
 * Was der Gast über den Online-Check-in gemeldet hat, für die Anreisen von heute.
 *
 * Getrennt geladen und nicht im Store: es betrifft eine Handvoll Buchungen am Tag,
 * und der Store würde sonst bei jedem Laden eine Tabelle mitschleppen, die neunzig
 * Prozent der Zeit niemanden interessiert.
 */
function useCheckins(bookingIds: string[]) {
  const store = useStore()
  const [map, setMap] = useState<Map<string, CheckinHint>>(new Map())
  const key = bookingIds.join(',')

  useEffect(() => {
    if (!store.live || !supabase || bookingIds.length === 0) { setMap(new Map()); return }
    let alive = true
    void supabase.from('guest_registrations')
      .select('booking_id, status, arrival_time, note')
      .in('booking_id', bookingIds)
      .then(({ data }) => {
        if (!alive) return
        const next = new Map<string, CheckinHint>()
        for (const r of (data ?? []) as CheckinRow[]) {
          next.set(r.booking_id, {
            status: r.status,
            arrivalTime: r.arrival_time ?? '',
            note: r.note ?? '',
          })
        }
        setMap(next)
      })
    return () => { alive = false }
  }, [key, store.live])

  return map
}

interface CheckinRow {
  booking_id: string
  status: 'open' | 'submitted' | 'checked_in'
  arrival_time: string | null
  note: string | null
}
interface CheckinHint {
  status: 'open' | 'submitted' | 'checked_in'
  arrivalTime: string
  note: string
}

function Section({
  title, icon, bookings, onPick, checkins,
}: {
  title: string; icon: React.ReactNode; bookings: Booking[]
  onPick: (b: Booking) => void
  checkins?: Map<string, CheckinHint>
}) {
  const { t, formatMoney } = useI18n()
  const store = useStore()

  return (
    <section>
      <h2 className="flex items-center gap-2 text-sm font-medium text-muted mb-2">
        {icon} {title}
        <span className="tabular-nums">({bookings.length})</span>
      </h2>
      {bookings.length === 0 ? (
        <Card><EmptyState title={t('common.noResults')} /></Card>
      ) : (
        <div className="space-y-2">
          {bookings.map((b) => (
            <button
              key={b.id}
              onClick={() => onPick(b)}
              className="w-full text-left"
            >
              <Card className="p-3 flex items-center gap-3 hover:bg-surface-2 transition-colors">
                <span className="w-1.5 h-10 rounded-full shrink-0"
                  style={{ background: store.unitById(b.unit_id)?.color }} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{store.guestName(b.guest_id)}</div>
                  <div className="text-xs text-muted truncate">
                    {store.unitById(b.unit_id)?.name} · {b.adults + b.children} {t('booking.occupancy')}
                    {/* Die vom Gast gemeldete Ankunftszeit — der einzige Grund, warum
                        jemand am Anreisetag zum Telefon greift. */}
                    {checkins?.get(b.id)?.arrivalTime && (
                      <span className="text-ok">
                        {' · '}{t('checkin.arrivingAround')} {checkins.get(b.id)!.arrivalTime}
                      </span>
                    )}
                  </div>
                  {checkins?.get(b.id)?.note && (
                    <div className="text-xs text-muted truncate">
                      {checkins.get(b.id)!.note}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <Badge tone={STATUS_TONE[b.status]}>{t(`status.${b.status}` as never)}</Badge>
                  <div className={cx('text-sm tabular-nums mt-1')}>{formatMoney(b.total_gross)}</div>
                </div>
                <ArrowRight size={16} className="text-muted shrink-0" />
              </Card>
            </button>
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * What has to be cleaned today.
 *
 * The list is derived from departures in the database, so it can never drift from the
 * bookings; only the "done" tick is stored. Same-day turnovers sort first and are
 * called out — that is the row where being late actually costs the landlord.
 */
function HousekeepingSection({ day }: { day: string }) {
  const { t, formatDate } = useI18n()
  const store = useStore()
  const [rows, setRows] = useState<api.HousekeepingRow[]>([])
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    if (!store.live || !supabase || !store.db.tenant.id) {
      // Demo mode: derive the same list from the in-browser bookings.
      const local = store.db.bookings
        .filter((b) => b.kind === 'booking' && b.departure === day
          && ['confirmed', 'checked_in', 'checked_out'].includes(b.status))
        .map((b) => {
          const next = store.db.bookings
            .filter((x) => x.unit_id === b.unit_id && x.arrival >= day
              && ['option', 'confirmed', 'checked_in'].includes(x.status))
            .sort((a, c) => a.arrival.localeCompare(c.arrival))[0]
          return {
            unit_id: b.unit_id,
            unit_name: store.unitById(b.unit_id)?.name ?? '—',
            property_name: '',
            booking_id: b.id,
            guest_name: store.guestName(b.guest_id),
            departed_on: b.departure,
            next_arrival: next?.arrival ?? null,
            turnover: next?.arrival === day,
            done: false,
            done_at: null,
          }
        })
        .sort((a, b) => Number(b.turnover) - Number(a.turnover))
      setRows(local)
      return
    }
    setLoading(true)
    try {
      setRows(await api.loadHousekeeping(supabase, store.db.tenant.id, day))
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [store, day])

  useEffect(() => { void load() }, [load])

  const toggle = async (row: api.HousekeepingRow) => {
    setRows((rs) => rs.map((r) =>
      r.unit_id === row.unit_id ? { ...r, done: !r.done } : r))
    if (!store.live || !supabase) return
    try {
      await api.setHousekeepingDone(
        supabase, store.db.tenant.id, row.unit_id, day, !row.done)
    } catch {
      void load()   // put the tick back where the server says it belongs
    }
  }

  const openCount = rows.filter((r) => !r.done).length

  return (
    <section>
      <h2 className="flex items-center gap-2 text-sm font-medium text-muted mb-2">
        <Sparkles size={16} /> {t('housekeeping.title')}
        <span className="tabular-nums">({openCount})</span>
      </h2>

      {rows.length === 0 ? (
        <Card>
          <EmptyState title={loading ? t('common.loading') : t('housekeeping.nothing')} />
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={r.unit_id} className={cx('p-3', r.done && 'opacity-60')}>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => toggle(r)}
                  aria-label={t('housekeeping.done')}
                  aria-pressed={r.done}
                  className={cx(
                    'w-9 h-9 rounded-xl shrink-0 flex items-center justify-center border',
                    r.done
                      ? 'bg-ok-soft text-ok border-ok/30'
                      : 'bg-surface-2 text-muted border-border',
                  )}
                >
                  <Check size={18} />
                </button>

                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{r.unit_name}</div>
                  <div className="text-xs text-muted truncate">
                    {t('housekeeping.departed')}: {r.guest_name || '—'}
                  </div>
                </div>

                <div className="text-right shrink-0">
                  {r.turnover ? (
                    <Badge tone="danger">{t('housekeeping.turnover')}</Badge>
                  ) : (
                    <Badge tone="neutral">
                      {r.next_arrival
                        ? `${t('housekeeping.nextArrival')} ${formatDate(r.next_arrival)}`
                        : t('housekeeping.noArrival')}
                    </Badge>
                  )}
                </div>
              </div>
              {r.turnover && !r.done && (
                <p className="text-xs text-danger mt-2">{t('housekeeping.turnoverHint')}</p>
              )}
            </Card>
          ))}
        </div>
      )}
    </section>
  )
}
